import axios from 'axios'
import { apiClient } from './client'
import { apiConfig, tiendanubeConfig } from '../config/api'

export interface AuthResponse {
  redirect_url: string
  state: string
}

export interface AuthResponseWrapper {
  success: boolean
  data?: AuthResponse
  error?: {
    message: string
    details?: string
    timestamp: string
  }
}

export interface CallbackResponse {
  success: boolean
  data?: {
    access_token: string
    user_id: string
    store_id: string
    store_name: string
    store_url?: string
    domains?: string[]
    original_domain?: string
  }
  error?: {
    message: string
    details?: string
    timestamp: string
  }
}

export interface Membership {
  store_id: string
  role: string
  granted_at?: string | null
}

export interface UserInfo {
  user_id: string
  store_id: string
  store_name: string
  store_url?: string
  domains?: string[]
  original_domain?: string
  email?: string
  auth_provider?: 'google' | 'tiendanube'
  memberships?: Membership[]
}

/**
 * Authentication Service for TiendaNube OAuth integration
 */
class AuthService {
  /** In-flight session renewal, so N concurrent 401s trigger exactly one refresh. */
  private refreshInFlight: Promise<boolean> | null = null

  /**
   * Initiate OAuth flow by calling the Lambda auth endpoint
   */
  async initiateAuth(flowType: 'user_auth' | 'app_install' = 'user_auth'): Promise<AuthResponse> {
    try {
      // Call API Gateway auth endpoint with flow type for user authentication
      const response = await apiClient.get<AuthResponse>(`/auth?flow_type=${flowType}`)
      return response
    } catch (error) {
      console.error('OAuth initiation failed:', error)
      throw error
    }
  }

  /**
   * Handle OAuth callback with authorization code
   */
  async handleCallback(code: string, state: string): Promise<CallbackResponse> {
    try {
      const response = await apiClient.get<CallbackResponse>(`/callback?code=${code}&state=${state}`)
      
      // If successful, store tokens and user info
      if (response.success && response.data) {
        this.storeAuthData(response.data)
      }
      
      return response
    } catch (error) {
      console.error('OAuth callback failed:', error)
      throw error
    }
  }

  /**
   * Store authentication data in localStorage
   */
  private storeAuthData(data: CallbackResponse['data']) {
    if (!data) return

    localStorage.setItem('tnx_access_token', data.access_token)
    localStorage.setItem('tnx_user_info', JSON.stringify({
      user_id: data.user_id,
      store_id: data.store_id,
      store_name: data.store_name,
      store_url: data.store_url,
      domains: data.domains,
      original_domain: data.original_domain
    }))

    // Set auth token in API client (deprecated - using interceptor now)
    // apiClient.setAuthToken(data.access_token)
  }

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem('tnx_access_token')
  }

  /**
   * Single source of truth for "currently-selected store_id".
   *
   * Resolution order:
   *   1. localStorage tnx_user_info.store_id (the user's explicit selection,
   *      written by bootstrap and StoreSwitcher), validated against JWT memberships
   *   2. First JWT membership — self-heals localStorage so subsequent reads agree
   *   3. Legacy TN OAuth user_data.store_id
   *   4. null — caller should treat as "not authenticated to a store"
   */
  getCurrentStoreId(): string | null {
    const payload = this.decodeJwtPayload()
    const memberships: Membership[] = Array.isArray(payload?.memberships) ? payload!.memberships : []

    // 1. Persisted user selection, validated against current memberships
    const persisted = this.readPersistedStoreId()
    if (persisted) {
      if (memberships.length === 0 || memberships.some(m => String(m.store_id) === persisted)) {
        return persisted
      }
      // Persisted store no longer in memberships — drop through to fallback
    }

    // 2. First membership (Google SSO)
    if (memberships.length > 0) {
      const fallback = String(memberships[0].store_id)
      this.writePersistedStoreId(fallback)
      return fallback
    }

    // 3. Legacy TN OAuth shape
    if (payload?.user_data?.store_id) {
      return String(payload.user_data.store_id)
    }

    return null
  }

  /**
   * Role of the current user for the currently-selected store, from JWT
   * memberships. Returns null when no membership matches (e.g. legacy TN OAuth
   * tokens, which have no role). Used to route merchants to the merchant view.
   */
  getCurrentRole(): string | null {
    const payload = this.decodeJwtPayload()
    const memberships: Membership[] = Array.isArray(payload?.memberships) ? payload!.memberships : []
    const storeId = this.getCurrentStoreId()
    if (!storeId) return null
    const m = memberships.find(x => String(x.store_id) === String(storeId))
    return m?.role ?? null
  }

  /** True when the current store membership marks this user as a merchant (vs consultant/owner). */
  isMerchant(): boolean {
    return this.getCurrentRole() === 'merchant'
  }

  /**
   * Get stored user info — extracts from JWT token.
   * Handles both shapes: legacy TN OAuth (user_data.store_id) and Google SSO
   * (memberships array). Current store_id is resolved via getCurrentStoreId().
   */
  getUserInfo(): UserInfo | null {
    const payload = this.decodeJwtPayload()
    if (payload) {
      // New shape: Google SSO with memberships array
      if (Array.isArray(payload.memberships)) {
        return {
          user_id: payload.sub,
          store_id: this.getCurrentStoreId() || '',
          store_name: payload.user_data?.name || 'ECL Desk',
          email: payload.email,
          auth_provider: payload.auth_provider || 'google',
          memberships: payload.memberships
        }
      }

      // Legacy shape: TN OAuth with user_data.store_id
      if (payload.user_data) {
        return {
          user_id: payload.sub,
          store_id: payload.user_data.store_id,
          store_name: payload.user_data.name || 'Store',
          store_url: payload.user_data.store_url,
          domains: payload.user_data.domains,
          original_domain: payload.user_data.original_domain,
          email: payload.email,
          auth_provider: 'tiendanube'
        }
      }
    }

    // Fallback to localStorage for backward compatibility
    const userInfoStr = localStorage.getItem('tnx_user_info')
    if (!userInfoStr) return null

    try {
      return JSON.parse(userInfoStr)
    } catch {
      return null
    }
  }

  private decodeJwtPayload(): any | null {
    const token = this.getAccessToken()
    if (!token) return null
    try {
      return JSON.parse(atob(token.split('.')[1]))
    } catch (error) {
      console.warn('Failed to decode JWT token:', error)
      return null
    }
  }

  private readPersistedStoreId(): string | null {
    try {
      const raw = localStorage.getItem('tnx_user_info')
      if (!raw) return null
      const parsed = JSON.parse(raw)
      return parsed?.store_id ? String(parsed.store_id) : null
    } catch {
      return null
    }
  }

  private writePersistedStoreId(storeId: string): void {
    try {
      const raw = localStorage.getItem('tnx_user_info')
      const existing = raw ? JSON.parse(raw) : {}
      existing.store_id = storeId
      localStorage.setItem('tnx_user_info', JSON.stringify(existing))
    } catch (err) {
      console.warn('Failed to persist current store_id:', err)
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken()
    const userInfo = this.getUserInfo()
    return !!(token && userInfo)
  }

  /**
   * Change the active store for a user who holds more than one membership
   * (an agency running several stores, or a consultant).
   *
   * Reloads rather than invalidating piecemeal: every screen is store-scoped
   * and much of the data is cached per store, so a reload is both simpler and
   * safer than hunting for stale caches. Same approach as StoreSwitcher.
   *
   * Refuses stores outside the token's memberships — getCurrentStoreId would
   * discard such a value on the next read anyway, leaving the UI lying about
   * which store it is showing.
   */
  switchStore(storeId: string): void {
    const target = String(storeId)
    const memberships = this.getUserInfo()?.memberships ?? []

    if (!memberships.some(m => String(m.store_id) === target)) {
      console.warn('Refusing to switch to a store outside memberships:', target)
      return
    }
    if (target === this.getCurrentStoreId()) return

    this.writePersistedStoreId(target)
    window.location.reload()
  }

  /**
   * Initialize auth state on app load
   */
  initializeAuth(): void {
    const token = this.getAccessToken()
    if (token) {
      // JWT tokens are now handled automatically by request interceptor
      console.log('JWT authentication initialized')
    }
  }

  /**
   * Logout - clear all auth data
   */
  logout(): void {
    localStorage.removeItem('tnx_access_token')
    localStorage.removeItem('tnx_refresh_token')
    localStorage.removeItem('tnx_user_info')
    // JWT tokens are handled by request interceptor now
  }

  /**
   * Start the Google SSO flow (default login path).
   * Redirects the browser to the API Gateway start endpoint, which 302s to Google.
   *
   * Merchant onboarding (B4): pass a `claim` (install claim) or `invite` token —
   * it rides the OAuth state param and auth-google-callback grants the scoped
   * merchant membership on return.
   */
  startGoogleLogin(params?: { claim?: string; invite?: string }): void {
    const qs = new URLSearchParams()
    if (params?.claim) qs.set('claim', params.claim)
    if (params?.invite) qs.set('invite', params.invite)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    window.location.href = `${apiConfig.baseURL}/auth/google/start${suffix}`
  }

  /**
   * Renew the session using the stored refresh token (POST /auth/refresh).
   *
   * Single-flighted: the server ROTATES refresh tokens, so two parallel calls
   * would race and one would invalidate the other. Every caller shares the one
   * in-flight promise.
   *
   * Returns true when new tokens are stored. On false the caller decides what
   * to do (client.ts clears auth and sends the user to login) — this method
   * deliberately does not log out on its own, so a transient network blip
   * during renewal isn't indistinguishable from a revoked session.
   */
  refreshSession(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight

    this.refreshInFlight = this.performRefresh().finally(() => {
      this.refreshInFlight = null
    })
    return this.refreshInFlight
  }

  private async performRefresh(): Promise<boolean> {
    // Read at call time, never from a closure: another tab may have rotated
    // the token since this request was queued.
    const refreshToken = localStorage.getItem('tnx_refresh_token')
    if (!refreshToken) return false

    try {
      // Bare axios, NOT apiClient — apiClient's response interceptor is what
      // calls this, and renewing through it would recurse.
      const res = await axios.post(
        `${apiConfig.baseURL}/auth/refresh`,
        { refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' }, timeout: apiConfig.timeout }
      )

      // Handler wraps as { success, data: {...} }; unwrap defensively.
      const data = res.data?.data ?? res.data
      if (!data?.access_token) return false

      localStorage.setItem('tnx_access_token', data.access_token)
      if (data.refresh_token) {
        localStorage.setItem('tnx_refresh_token', data.refresh_token)
      }
      // tnx_user_info is left alone on purpose: it carries the user's selected
      // store, which must survive a renewal. Memberships come from the token.
      console.log('Session renewed')
      return true
    } catch (error) {
      console.warn('Session renewal failed:', error)
      return false
    }
  }

  /**
   * Persist tokens + selected store after Google SSO callback.
   * Called by AuthCallbackPage when it sees ?access_token=...&flow=google.
   */
  bootstrapGoogleSession(accessToken: string, refreshToken: string): void {
    localStorage.setItem('tnx_access_token', accessToken)
    if (refreshToken) {
      localStorage.setItem('tnx_refresh_token', refreshToken)
    }

    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]))
      const memberships: Membership[] = Array.isArray(payload.memberships) ? payload.memberships : []

      // Preserve previously-selected store if it's still in memberships; otherwise pick the first.
      const persisted = this.readPersistedStoreId()
      const currentStoreId = persisted && memberships.some(m => String(m.store_id) === persisted)
        ? persisted
        : (memberships[0]?.store_id ? String(memberships[0].store_id) : '')

      const userInfo = {
        user_id: payload.sub,
        store_id: currentStoreId,
        store_name: payload.user_data?.name || 'ECL Desk',
        email: payload.email,
        auth_provider: 'google'
      }
      localStorage.setItem('tnx_user_info', JSON.stringify(userInfo))
    } catch (err) {
      console.warn('Failed to decode Google JWT during bootstrap:', err)
    }
  }

  /**
   * Start User Login OAuth flow (creates JWT session)
   * This will return the redirect URL that we should redirect the user to
   */
  async startUserLoginFlow(): Promise<void> {
    try {
      const authResponse = await this.initiateAuth('user_auth')

      if (authResponse.redirect_url) {
        // Redirect to the OAuth URL provided by the backend
        window.location.href = authResponse.redirect_url
      } else {
        throw new Error('Failed to get redirect URL from auth endpoint')
      }
    } catch (error) {
      console.error('Failed to start user login flow:', error)
      throw error
    }
  }

  /**
   * Start App Installation OAuth flow (stores TN access token)
   * This will return the redirect URL that we should redirect the user to
   */
  async startAppInstallFlow(): Promise<void> {
    try {
      const authResponse = await this.initiateAuth('app_install')

      if (authResponse.redirect_url) {
        // Redirect to the OAuth URL provided by the backend
        window.location.href = authResponse.redirect_url
      } else {
        throw new Error('Failed to get redirect URL from auth endpoint')
      }
    } catch (error) {
      console.error('Failed to start app install flow:', error)
      throw error
    }
  }

  /**
   * @deprecated Use startUserLoginFlow() instead
   * Start OAuth flow by calling the Lambda auth endpoint
   */
  async startOAuthFlow(): Promise<void> {
    return this.startUserLoginFlow()
  }

  /**
   * Redirect to TiendaNube OAuth (direct approach if needed)
   */
  redirectToTiendaNubeAuth(): void {
    const params = new URLSearchParams({
      client_id: tiendanubeConfig.appId,
      redirect_uri: tiendanubeConfig.redirectUri,
      response_type: 'code',
      state: this.generateState(),
      scope: 'read_content write_content read_orders read_products write_products'
    })

    const authUrl = `${tiendanubeConfig.authUrl}?${params.toString()}`
    window.location.href = authUrl
  }

  /**
   * Generate random state for OAuth security
   */
  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15)
  }
}

export const authService = new AuthService()